import React, { useState, useContext } from "react";
import _ from "lodash";
import { Nav, NavItem, NavLink } from "reactstrap";
import {
  UncontrolledDropdown,
  DropdownItem,
  DropdownToggle,
  DropdownMenu,
} from "reactstrap";
import {
  confirmDiscard,
  useChildScope,
  ScopeProvider,
} from "@repoflow/components/unsaved-changes";
import "./style.css";

export const useContentState = ({ selected }) => {
  const [state, setState] = useState({ selected, subitem: null });

  return {
    selected: state.selected,
    subitem: state.subitem,
    select: (sel, subitem = null) => setState({ selected: sel, subitem }),
    selectByID: (id) => {},
  };
};

const Context = React.createContext(null);

export const useTabsContext = () => {
  return useContext(Context);
};

export default ({
  control,
  navs,
  tabs,
  selected: initial = 0,
  frame: Frame,
}) => {
  const defaultControl = useContentState({ selected: initial });
  const currControl = control ? control : defaultControl;
  const { subitem, selected, select } = currControl;
  const selectedTab = tabs[selected] || tabs[0];

  // Switching tabs unmounts the body (`key={selected}` below) and every form
  // in it, so a click on another tab asks first when one of THOSE is dirty —
  // and only those: a form the tabs sit inside, as on the site screen and in
  // the page editor, survives the switch and is not this strip's business.
  // Only the clicks ask; a `control` selecting programmatically does not.
  const body = useChildScope();
  const choose = (idx, sub = null) => {
    if (idx === selected && sub === subitem) return;
    if (!confirmDiscard(body)) return;
    select(idx, sub);
  };

  const content =
    subitem !== null
      ? selectedTab.subitems[subitem].content
      : selectedTab.content;

  return (
    <Context.Provider value={currControl}>
      <>
        <div className="float-right">
          {
            <Nav selected={selected} tabs={true}>
              <MenuItems
                selected={selected}
                select={choose}
                align={"right"}
                tabs={tabs}
              />{" "}
            </Nav>
          }
        </div>
        {
          <Nav selected={selected} tabs={true}>
            {navs &&
              _.map(navs, ({ label, subitems, onClick }, idx) => (
                <NavItem key={idx}>
                  {subitems ? (
                    <UncontrolledDropdown nav>
                      <DropdownToggle nav caret>
                        {label}
                      </DropdownToggle>
                      <DropdownMenu>
                        {_.map(subitems, (curr, subidx) => {
                          const { label, onClick } = curr;

                          return (
                            <DropdownItem
                              key={subidx}
                              onClick={(e) => {
                                onClick && onClick(e);
                                return false;
                              }}
                            >
                              {label}
                            </DropdownItem>
                          );
                        })}
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  ) : (
                    <NavLink
                      active={selected === idx}
                      href="#"
                      onClick={(e) => {
                        onClick && onClick(e);
                        return false;
                      }}
                    >
                      {label}
                    </NavLink>
                  )}
                </NavItem>
              ))}

            <MenuItems
              selected={selected}
              select={choose}
              align={"left"}
              tabs={tabs}
            />
          </Nav>
        }

        {/* The gap between the strip and whatever the tab renders lives
            here, once, rather than as an `mt-3` on the first Row of every tab
            body. Half the tabs in the app had it and half did not, so a form
            opened flush against the bottom border of its own tab — which is
            what it looked like on the site screen.

            Inside the frame, not around it: the page editor's frame is a
            scroll pane whose height is computed from its own top, and padding
            above it would push the pane off the bottom instead of moving the
            content down inside it. */}
        {Frame ? (
          <Frame key={selected}>
            <div className="content-tabs-body">
              <ScopeProvider scope={body}>{content}</ScopeProvider>
            </div>
          </Frame>
        ) : (
          <div className="content-tabs-body" key={selected}>
            <ScopeProvider scope={body}>{content}</ScopeProvider>
          </div>
        )}
      </>
    </Context.Provider>
  );
};

const MenuItems = ({ tabs, selected, select, align: aligned }) =>
  _.map(tabs, ({ label, subitems, onClick, align = "left" }, idx) =>
    align === aligned ? (
      <NavItem key={idx}>
        {subitems ? (
          <UncontrolledDropdown nav>
            <DropdownToggle nav caret>
              {label}
            </DropdownToggle>
            <DropdownMenu>
              {_.map(subitems, (curr, subidx) => {
                const { label } = curr;

                return (
                  <DropdownItem
                    key={subidx}
                    onClick={() => {
                      select(idx, subidx);
                      return false;
                    }}
                  >
                    {label}
                  </DropdownItem>
                );
              })}
            </DropdownMenu>
          </UncontrolledDropdown>
        ) : (
          <NavLink
            active={selected === idx}
            href="#"
            onClick={(e) => {
              select(idx);
              onClick && onClick(e);
              return false;
            }}
          >
            {label}
          </NavLink>
        )}
      </NavItem>
    ) : null
  );
